if (!customElements.get('dc-product-details')) {
  class DcProductDetails extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-tabs') || this._isConnected) {
        return;
      }

      this._isConnected = true;
      this._tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this._transitionId = 0;
      this._panelAnimation = null;
      this._onClick = this._handleClick.bind(this);
      this._onKeydown = this._handleKeydown.bind(this);
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);
    }

    disconnectedCallback() {
      if (!this._isConnected) {
        return;
      }

      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeydown);
      this._transitionId += 1;
      this._panelAnimation?.cancel();
      this._panelAnimation = null;
      this._isConnected = false;
    }

    _handleClick(event) {
      const tab = event.target.closest('[role="tab"]');

      if (!tab || !this.contains(tab)) {
        return;
      }

      this._activateTab(tab, false);
    }

    _handleKeydown(event) {
      const tab = event.target.closest('[role="tab"]');

      if (!tab || !this.contains(tab)) {
        return;
      }

      const currentIndex = this._tabs.indexOf(tab);
      let nextIndex = currentIndex;
      const isRtl = document.documentElement.dir === 'rtl';

      if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + (isRtl ? -1 : 1) + this._tabs.length) % this._tabs.length;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = (currentIndex + (isRtl ? 1 : -1) + this._tabs.length) % this._tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this._tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this._activateTab(this._tabs[nextIndex], true);
    }

    async _activateTab(activeTab, moveFocus) {
      const nextPanel = this.querySelector(`#${CSS.escape(activeTab.getAttribute('aria-controls'))}`);
      const currentPanel = this.querySelector('.dc-product-details__panel:not([hidden])');
      const transitionId = this._transitionId + 1;

      this._transitionId = transitionId;
      this._panelAnimation?.cancel();
      this._panelAnimation = null;

      this._tabs.forEach((tab) => {
        const isActive = tab === activeTab;

        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });

      if (moveFocus) {
        activeTab.focus();
      }

      if (!nextPanel) {
        return;
      }

      const panels = Array.from(this.querySelectorAll('.dc-product-details__panel'));
      const showNextPanel = () => {
        panels.forEach((panel) => {
          panel.hidden = panel !== nextPanel;
        });
      };

      if (currentPanel === nextPanel) {
        showNextPanel();
        return;
      }

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reduceMotion || !currentPanel || typeof currentPanel.animate !== 'function') {
        showNextPanel();
        return;
      }

      this._panelAnimation = currentPanel.animate(
        [{ opacity: 1 }, { opacity: 0 }],
        { duration: 90, easing: 'ease-in', fill: 'both' },
      );

      try {
        await this._panelAnimation.finished;
      } catch (_error) {
        return;
      }

      if (transitionId !== this._transitionId) {
        return;
      }

      this._panelAnimation.cancel();
      showNextPanel();

      this._panelAnimation = nextPanel.animate(
        [
          { opacity: 0, transform: 'translateY(6px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: 150, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)', fill: 'both' },
      );

      try {
        await this._panelAnimation.finished;
      } catch (_error) {
        return;
      }

      if (transitionId === this._transitionId) {
        this._panelAnimation.cancel();
        this._panelAnimation = null;
      }
    }
  }

  customElements.define('dc-product-details', DcProductDetails);
}
